// SCADA Entegrasyonu — kaynaklar, sayfalar, API anahtarları ve etiketler.
//
// Referans HTML'i YOK (ekran 01–14 dışında yeni bir modül). Sınıfların tamamı
// design-system.css'te tanımlı; yeni sınıf uydurulmadı.
//
// YERLEŞİM (kullanıcı kararı, docs/kararlar-ek.md): sol ağaç sayfanın ~%25'i,
// kalan alan detay. Paylaşılan `.list-grid` ölçüsü (1fr / 396 px) burada satır
// içi grid-template-columns ile eziliyor — çünkü bu ekranın sağ tarafı bir
// "muayene paneli" değil, tabloların yaşadığı asıl çalışma alanı. Diğer
// ekranlar etkilenmesin diye karar CSS'e değil bu dosyaya yazıldı.
//
// Ağaç kurgusu: sol tablo KAYNAK satırlarını, açılmış kaynakların altında da
// SAYFA satırlarını gösterir. Sayfa listesi ancak kaynak detayında geliyor
// (GET /kaynaklar sayaç veriyor, satır değil) — bu yüzden detay yalnız açılan
// kaynak için çekiliyor; hepsini peşin çekmek her açılışta N istek olurdu.

import { Fragment, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useQueries, useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import Kabuk from '../../components/Kabuk'
import { useDrawer } from '../../components/Drawer'
import { tr, trTarihSaat } from '../../lib/trFormat'
import { useYetki, YetkiKapisi } from '../../lib/yetki'
import AnahtarDrawer from './AnahtarDrawer'
import Baglanti from './Baglanti'
import EtiketDrawer from './EtiketDrawer'
import PaketlerDrawer from './PaketlerDrawer'
import { AnahtarTablosu, DurumRozeti, EtiketTablosu, SayfaTablosu } from './Tablolar'
import { KaynakDrawer, SayfaDrawer } from './TanimDrawer'
import { akisDurumu, yasMetni } from './bicim'
import {
  entegrasyon,
  type AnahtarSatiri,
  type EntegrasyonDurumu,
  type KaynakDetay,
  type KaynakSatiri,
  type SayfaSatiri,
} from './api'
import SayfaSilDrawer from './SayfaSilDrawer'

/** Akan veri: liste ve seçili detay kendiliğinden tazelenir (ms). */
const TAZELEME = 30_000

/** Sol ağaç sayfanın dörtte biri; altında okunmaz olacağı için taban da var. */
const YERLESIM = { gridTemplateColumns: 'minmax(260px, 25%) minmax(0, 1fr)' }

/** Ağaç uzun olduğunda kaybolmasın — sağdaki tablolar kendi başına kayar. */
const AGAC: React.CSSProperties = {
  position: 'sticky',
  top: 0,
  maxHeight: 'calc(100vh - 118px)',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
}

type Segment = 'tumu' | EntegrasyonDurumu

const SEGMENTLER: { anahtar: Segment; etiket: string }[] = [
  { anahtar: 'tumu', etiket: 'Tümü' },
  { anahtar: 'aktif', etiket: 'Aktif' },
  { anahtar: 'pasif', etiket: 'Pasif' },
]

type Secim = { tur: 'kaynak' | 'sayfa'; id: string }

const CHEVRON = (acik: boolean) => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d={acik ? 'm6 9 6 6 6-6' : 'm9 18 6-6-6-6'} />
  </svg>
)

/**
 * Sayı + birim. Değer yoksa birim de yazılmaz: "— %" kullanıcıya sıfır gibi
 * görünüyor, oysa ajan o alanı hiç göndermemiş olabilir (§3.6).
 */
function olcu(deger: number | null | undefined, basamak: number, birim?: string): string {
  if (deger === null || deger === undefined) return '—'
  return birim ? `${tr(deger, basamak)} ${birim}` : tr(deger, basamak)
}

/** Akış/sağlık gibi ad–değer listelerinin ortak tablosu. */
function OlcuTablosu({ satirlar }: { satirlar: { ad: string; deger: ReactNode; not?: string }[] }) {
  return (
    <table className="grid">
      <thead>
        <tr>
          <th style={{ width: 190 }}>Gösterge</th>
          <th style={{ width: 210 }}>Değer</th>
          <th>Açıklama</th>
        </tr>
      </thead>
      <tbody>
        {satirlar.map((s) => (
          <tr key={s.ad}>
            <td>{s.ad}</td>
            <td className="mono" style={{ fontSize: 11.5, fontWeight: 600 }}>
              {s.deger}
            </td>
            <td className="cell-sub" style={{ fontSize: 10.5 }}>
              {s.not ?? ''}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

type Sekme = { anahtar: string; etiket: string; sayac?: number; icerik: ReactNode }

/**
 * Detay alanının kabuğu: başlık + eylemler + sekmeler.
 *
 * Sekmelere geçildi çünkü tek sütunda alt alta duran altı bölüm (uç nokta,
 * akış, sağlık, anahtarlar, sayfalar, etiketler) "neyi nereden okuyacağım"
 * sorusunu doğuruyordu. Artık ilk sekme kurulum için gerekenin TAMAMI, diğerleri
 * işletme görünümleri.
 */
function Detay({
  eyebrow,
  baslik,
  altYazi,
  eylemler,
  sekmeler,
  secili,
  sec,
}: {
  eyebrow: ReactNode
  baslik: ReactNode
  altYazi?: ReactNode
  eylemler: ReactNode
  sekmeler: Sekme[]
  secili: string
  sec: (a: string) => void
}) {
  const etkin = sekmeler.find((s) => s.anahtar === secili) ?? sekmeler[0]

  return (
    <div className="card" style={{ minWidth: 0 }}>
      <div className="insp-h" style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="insp-eyebrow">{eyebrow}</div>
          <div className="insp-t">{baslik}</div>
          {altYazi && (
            <div className="cell-sub" style={{ marginTop: 2 }}>
              {altYazi}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>{eylemler}</div>
      </div>

      <div className="tabbar">
        {sekmeler.map((s) => (
          <button
            key={s.anahtar}
            type="button"
            className={etkin.anahtar === s.anahtar ? 'tab on' : 'tab'}
            onClick={() => sec(s.anahtar)}
          >
            {s.etiket}
            {s.sayac !== undefined && (
              <span className={`badge ${etkin.anahtar === s.anahtar ? 'b-navy' : 'b-gray'}`}>
                {tr(s.sayac, 0)}
              </span>
            )}
          </button>
        ))}
      </div>

      <div style={{ padding: etkin.anahtar === 'baglanti' ? 12 : 0 }}>{etkin.icerik}</div>
    </div>
  )
}

// -------------------------------------------------------------------- ekran

function Icerik() {
  const [segment, setSegment] = useState<Segment>('tumu')
  const [arama, setArama] = useState('')
  const [acik, setAcik] = useState<string[]>([])
  const [secim, setSecim] = useState<Secim | null>(null)
  const [sekme, setSekme] = useState('baglanti')
  const { ac } = useDrawer()
  const git = useNavigate()
  const { izinVar } = useYetki()
  const yazabilir = izinVar('Entegrasyon', 'yaz')

  const { data: kaynaklar = [], isLoading } = useQuery({
    queryKey: ['entegrasyon-kaynaklar'],
    queryFn: entegrasyon.kaynaklar,
    refetchInterval: TAZELEME,
  })

  const liste = useMemo(
    () =>
      kaynaklar.filter((k) => {
        if (segment !== 'tumu' && k.durum !== segment) return false
        if (arama) {
          const a = arama.toLocaleLowerCase('tr')
          if (![k.kod, k.ad, k.aciklama ?? ''].join(' ').toLocaleLowerCase('tr').includes(a))
            return false
        }
        return true
      }),
    [kaynaklar, segment, arama],
  )

  // İlk kaynak kendiliğinden açılır: tek kaynaklı tesiste (olağan hâl) kullanıcı
  // hiçbir şey tıklamadan ağacı ve detayı dolu bulsun.
  const ilkId = liste[0]?.id
  useEffect(() => {
    if (!ilkId) return
    setAcik((a) => (a.length === 0 ? [ilkId] : a))
    setSecim((s) => s ?? { tur: 'kaynak', id: ilkId })
  }, [ilkId])

  const seciliSayfaId = secim?.tur === 'sayfa' ? secim.id : null

  const { data: sayfaDetay } = useQuery({
    queryKey: ['entegrasyon-sayfa', seciliSayfaId],
    queryFn: () => entegrasyon.sayfa(seciliSayfaId!),
    enabled: !!seciliSayfaId,
    refetchInterval: TAZELEME,
  })

  /**
   * Detayı çekilecek kaynaklar: ağaçta AÇIK olanlar + seçimin bağlı olduğu
   * kaynak.
   *
   * Seçim ayrıca ekleniyor çünkü açma/kapama YALNIZ ağaçtaki sayfa satırlarını
   * gösterip gizlemeli. Sağ alanın ihtiyacı (anahtar listesi, kaynak kodu, ajan
   * sağlığı) kaynak detayından geliyor — sayfa ucu yalnız etiketleri döndürüyor.
   */
  const detayIdleri = useMemo(() => {
    const kume = new Set(acik)
    if (secim?.tur === 'kaynak') kume.add(secim.id)
    if (sayfaDetay) kume.add(sayfaDetay.sayfa.kaynakId)
    return [...kume]
  }, [acik, secim, sayfaDetay])

  const detaySorgulari = useQueries({
    queries: detayIdleri.map((id) => ({
      queryKey: ['entegrasyon-kaynak', id],
      queryFn: () => entegrasyon.kaynak(id),
      refetchInterval: TAZELEME,
    })),
  })

  const detaylar = useMemo(() => {
    const m = new Map<string, KaynakDetay>()
    for (const s of detaySorgulari) if (s.data) m.set(s.data.kaynak.id, s.data)
    return m
  }, [detaySorgulari])

  const kaynakAc = (id: string) =>
    setAcik((a) => (a.includes(id) ? a.filter((x) => x !== id) : [...a, id]))

  // Seçim değişince sekme başa dönüyor: yeni bir sayfada ilk soru daima
  // "buraya nasıl veri gönderilir".
  const secimYap = (s: Secim) => {
    setSecim(s)
    setSekme('baglanti')
  }

  // KPI'lar filtreden BAĞIMSIZ — tesisin bütününü söylüyorlar.
  const toplam = useMemo(() => {
    const yaslar = kaynaklar
      .map((k) => k.sonPaketYasiSn)
      .filter((y): y is number => y !== null)
    return {
      kaynak: kaynaklar.length,
      aktif: kaynaklar.filter((k) => k.durum === 'aktif').length,
      sayfa: kaynaklar.reduce((t, k) => t + k.sayfaSayisi, 0),
      etiket: kaynaklar.reduce((t, k) => t + k.etiketSayisi, 0),
      paket: kaynaklar.reduce((t, k) => t + k.paketSayisi, 0),
      anahtar: kaynaklar.reduce((t, k) => t + k.anahtarSayisi, 0),
      enTazeYas: yaslar.length ? Math.min(...yaslar) : null,
    }
  }, [kaynaklar])

  const sayac = (a: Segment) =>
    a === 'tumu' ? kaynaklar.length : kaynaklar.filter((k) => k.durum === a).length

  const satirlar: { kaynak: KaynakSatiri; sayfalar: SayfaSatiri[] }[] = liste.map((k) => ({
    kaynak: k,
    sayfalar: acik.includes(k.id) ? (detaylar.get(k.id)?.sayfalar ?? []) : [],
  }))

  const seciliKaynakDetay =
    secim?.tur === 'kaynak'
      ? detaylar.get(secim.id)
      : sayfaDetay
        ? detaylar.get(sayfaDetay.sayfa.kaynakId)
        : undefined

  // -------------------------------------------------------- detay içerikleri

  function kaynakDetayi(detay: KaynakDetay) {
    const k = detay.kaynak
    const akis = akisDurumu(k.sonPaketYasiSn)
    const saglik = detay.sonSaglik

    const sekmeler: Sekme[] = [
      {
        anahtar: 'baglanti',
        etiket: 'Bağlantı',
        icerik: (
          <Baglanti
            url={k.ucNoktaUrl}
            saha={k.kod}
            sayfaKodu={null}
            anahtarlar={detay.anahtarlar}
            yazabilir={yazabilir}
            yeniAnahtar={() => yeniAnahtarAc(k.id, null, k.kod, 'kaynağın tamamı')}
          />
        ),
      },
      {
        anahtar: 'sayfalar',
        etiket: 'Sayfalar',
        sayac: detay.sayfalar.length,
        icerik: (
          <SayfaTablosu
            sayfalar={detay.sayfalar}
            yazabilir={yazabilir}
            sec={(id) => secimYap({ tur: 'sayfa', id })}
            sayfaEkle={() =>
              ac({
                baslik: 'Sayfa tanımla',
                altBaslik: `${k.kod} · paketteki page.id`,
                boyut: 'md',
                icerik: <SayfaDrawer kaynakId={k.id} />,
              })
            }
          />
        ),
      },
      {
        anahtar: 'anahtarlar',
        etiket: 'API Anahtarları',
        sayac: detay.anahtarlar.length,
        icerik: (
          <AnahtarTablosu
            anahtarlar={detay.anahtarlar}
            yazabilir={yazabilir}
            yeniAnahtar={() => yeniAnahtarAc(k.id, null, k.kod, 'kaynağın tamamı')}
          />
        ),
      },
      {
        anahtar: 'akis',
        etiket: 'Akış & Sağlık',
        icerik: (
          <OlcuTablosu
            satirlar={[
              {
                ad: 'Son paket',
                deger: k.sonPaketZamani ? trTarihSaat(k.sonPaketZamani) : '—',
                not: 'Sunucunun alınma damgası',
              },
              { ad: 'Yaş', deger: yasMetni(k.sonPaketYasiSn), not: akis.baslik },
              {
                ad: 'Son sıra no (seq)',
                deger: k.sonSeq === null ? '—' : tr(k.sonSeq, 0),
                not: 'Boşluk normaldir — ölü mektuba düşen numaralar hiç gelmez',
              },
              { ad: 'Paket sayısı', deger: tr(k.paketSayisi, 0), not: 'Tekilleştirme sonrası' },
              {
                ad: 'Ajan sürümü',
                deger: k.ajanSurumu ?? '—',
                not: 'Zarftaki agent alanı',
              },
              ...(saglik
                ? [
                    { ad: 'Ajan CPU', deger: olcu(saglik.cpu_pct, 2, '%'), not: 'Ajan sürecinin payı' },
                    { ad: 'Ajan bellek', deger: olcu(saglik.ram_mb, 1, 'MB'), not: '' },
                    {
                      ad: 'GDI tanıtıcı',
                      deger: olcu(saglik.gdi, 0),
                      not: 'Sızıntı kanaryası (§3.1)',
                    },
                    {
                      ad: 'Kaynak uyarısı',
                      deger: saglik.resource_warning ? (
                        <span className="badge b-red">Ajan kendi eşiğini aştı</span>
                      ) : (
                        'Yok'
                      ),
                      not: '',
                    },
                  ]
                : []),
            ]}
          />
        ),
      },
    ]

    return (
      <Detay
        secili={sekme}
        sec={setSekme}
        sekmeler={sekmeler}
        eyebrow={
          <>
            <span className="kod">{k.kod}</span>
            <span>·</span>
            <span>SCADA kaynağı</span>
            {k.ajanSurumu && (
              <>
                <span>·</span>
                <span className="mono">{k.ajanSurumu}</span>
              </>
            )}
          </>
        }
        baslik={
          <>
            {k.ad}{' '}
            <span className={`badge ${akis.sinif}`} title={akis.baslik}>
              {akis.etiket}
            </span>{' '}
            <DurumRozeti durum={k.durum} />
          </>
        }
        altYazi={k.aciklama}
        eylemler={
          <>
            <button
              className="btn btn-ghost btn-sm"
              type="button"
              title="Bu kaynağın son ham paketleri"
              onClick={() =>
                ac({
                  baslik: 'Ham paketler',
                  altBaslik: `${k.kod} · tanı listesi`,
                  boyut: 'md',
                  icerik: <PaketlerDrawer kaynakId={k.id} />,
                })
              }
            >
              Ham paketler
            </button>
            <button
              className="btn btn-sec btn-sm"
              type="button"
              disabled={!yazabilir}
              title={yazabilir ? 'Kaynağı düzenle' : 'Bu modülde yazma yetkiniz yok'}
              onClick={() =>
                ac({
                  baslik: 'Kaynağı düzenle',
                  altBaslik: k.kod,
                  boyut: 'md',
                  icerik: <KaynakDrawer mevcut={k} />,
                })
              }
            >
              Düzenle
            </button>
          </>
        }
      />
    )
  }

  function yeniAnahtarAc(
    kaynakId: string,
    sayfaId: string | null,
    kapsamAdi: string,
    kapsamNotu: string,
  ) {
    ac({
      baslik: 'Yeni API anahtarı',
      altBaslik: `${kapsamAdi} · ${kapsamNotu}`,
      boyut: 'md',
      icerik: (
        <AnahtarDrawer
          kaynakId={kaynakId}
          sayfaId={sayfaId ?? undefined}
          kapsamAdi={kapsamAdi}
        />
      ),
    })
  }

  // -------------------------------------------------------------- işaretleme

  return (
    <>
      <div className="crumb">
        Ana Veri <b>/ SCADA Entegrasyonu</b>
      </div>

      <div className="page-h">
        <h1 className="page-t">SCADA Entegrasyonu — Kaynaklar ve Sayfalar</h1>
        <div className="page-actions">
          {/* Modülün iki görünümü: kurulum ağacı ve akan etiketlerin tamamı. */}
          <div className="seg">
            <button className="seg-i on" type="button">
              Kaynaklar
            </button>
            <button
              className="seg-i"
              type="button"
              onClick={() => git('/entegrasyon/etiketler')}
            >
              Etiketler
              <small>{tr(toplam.etiket, 0)}</small>
            </button>
          </div>
          <button
            className="btn btn-ghost"
            type="button"
            title="Tüm kaynakların son ham paketleri"
            onClick={() =>
              ac({
                baslik: 'Ham paketler',
                altBaslik: 'Tüm kaynaklar · tanı listesi',
                boyut: 'md',
                icerik: <PaketlerDrawer />,
              })
            }
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zM14 2v6h6M9 13h6M9 17h6" />
            </svg>
            Ham paketler
          </button>
          <button
            className="btn btn-pri"
            type="button"
            disabled={!yazabilir}
            title={yazabilir ? undefined : 'Bu modülde yazma yetkiniz yok'}
            onClick={() =>
              ac({
                baslik: 'Yeni SCADA kaynağı',
                altBaslik: 'Ajanın site alanına karşılık gelir',
                boyut: 'md',
                icerik: <KaynakDrawer />,
              })
            }
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 5v14M5 12h14" />
            </svg>
            Kaynak ekle
          </button>
        </div>
      </div>

      <div className="kstrip">
        <div className="k">
          <div className="k-l">Kaynak</div>
          <div className="k-v">{tr(toplam.kaynak, 0)}</div>
          <div className="k-s">{tr(toplam.aktif, 0)} aktif · SCADA operatör PC'si</div>
        </div>
        <div className="k">
          <div className="k-l">Sayfa</div>
          <div className="k-v">{tr(toplam.sayfa, 0)}</div>
          <div className="k-s">tanınan SCADA ekranı</div>
        </div>
        <div className="k">
          <div className="k-l">Etiket</div>
          <div className="k-v">{tr(toplam.etiket, 0)}</div>
          <div className="k-s">okuma noktası · OCR + renk-durum</div>
        </div>
        <div className="k">
          <div className="k-l">En Taze Akış</div>
          <div className="k-v">{yasMetni(toplam.enTazeYas)}</div>
          <div className="k-s">son paketten bu yana</div>
        </div>
        <div className="k">
          <div className="k-l">Paket</div>
          <div className="k-v">{tr(toplam.paket, 0)}</div>
          <div className="k-s">{tr(toplam.anahtar, 0)} API anahtarı</div>
        </div>
      </div>

      <div className="list-grid" style={YERLESIM}>
        <div className="card" style={AGAC}>
          <div className="toolbar">
            <div className="seg">
              {SEGMENTLER.map((s) => (
                <button
                  key={s.anahtar}
                  type="button"
                  className={segment === s.anahtar ? 'seg-i on' : 'seg-i'}
                  onClick={() => setSegment(s.anahtar)}
                >
                  {s.etiket}
                  <small>{sayac(s.anahtar)}</small>
                </button>
              ))}
            </div>
            <div className="f-search" style={{ width: '100%' }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8" />
                <path d="M21 21l-4.35-4.35" />
              </svg>
              <input
                placeholder="Kaynak kodu, ad ara…"
                value={arama}
                onChange={(e) => setArama(e.target.value)}
              />
            </div>
          </div>

          <div style={{ overflow: 'auto', flex: 1, minHeight: 0 }}>
            {/*
              Sabit yerleşim: ağaç sütunu dar (sayfanın %25'i) ve içerik
              genişliği kolonları zorlarsa yatay kaydırma çubuğu doğuyordu.
              `table-layout: fixed` ile kolonlar bildirilen ölçüde kalıyor,
              uzun kodlar satır sonunda kırılıyor.
            */}
            <table className="grid" style={{ tableLayout: 'fixed', width: '100%' }}>
              <thead>
                <tr>
                  <th style={{ width: 30, padding: '6px 2px' }} />
                  <th>Kaynak / Sayfa</th>
                  <th style={{ width: 78 }}>Akış</th>
                </tr>
              </thead>
              <tbody>
                {isLoading && (
                  <tr>
                    <td colSpan={3} className="cell-sub" style={{ padding: 14 }}>
                      Yükleniyor…
                    </td>
                  </tr>
                )}
                {!isLoading && liste.length === 0 && (
                  <tr>
                    <td colSpan={3} className="cell-sub" style={{ padding: 14 }}>
                      {kaynaklar.length === 0
                        ? 'Henüz kaynak yok — ajanın göndereceği site için bir kaynak açın.'
                        : 'Süzgece uyan kaynak yok.'}
                    </td>
                  </tr>
                )}

                {satirlar.map(({ kaynak: k, sayfalar }) => {
                  const akis = akisDurumu(k.sonPaketYasiSn)
                  // Kaynak ve sayfa satırları AYNI tbody'nin kardeşleri olmalı;
                  // araya sarmalayıcı bir eleman girerse tablo düzeni bozulur.
                  return (
                    <Fragment key={k.id}>
                      <tr
                        className={secim?.tur === 'kaynak' && secim.id === k.id ? 'sel' : ''}
                        onClick={() => secimYap({ tur: 'kaynak', id: k.id })}
                      >
                        <td style={{ padding: '5px 2px' }}>
                          <button
                            className="mini-btn"
                            type="button"
                            title={acik.includes(k.id) ? 'Sayfaları gizle' : 'Sayfaları göster'}
                            onClick={(e) => {
                              e.stopPropagation()
                              kaynakAc(k.id)
                            }}
                          >
                            {CHEVRON(acik.includes(k.id))}
                          </button>
                        </td>
                        <td>
                          <div className="cell-name">
                            {k.ad}
                            {k.durum === 'pasif' && <span className="badge b-gray">Pasif</span>}
                          </div>
                          <div className="cell-sub" style={{ overflowWrap: 'anywhere' }}>
                            <span className="kod">{k.kod}</span> · {tr(k.sayfaSayisi, 0)} sayfa ·{' '}
                            {tr(k.anahtarSayisi, 0)} anahtar
                          </div>
                        </td>
                        <td>
                          <span className={`badge ${akis.sinif}`} title={akis.baslik}>
                            {akis.etiket}
                          </span>
                        </td>
                      </tr>

                      {sayfalar.map((s) => {
                        const sAkis = akisDurumu(s.sonGorulmeYasiSn)
                        return (
                          <tr
                            key={s.id}
                            className={secim?.tur === 'sayfa' && secim.id === s.id ? 'sel' : ''}
                            onClick={() => secimYap({ tur: 'sayfa', id: s.id })}
                          >
                            <td />
                            <td style={{ paddingLeft: 20 }}>
                              <div className="cell-name">
                                {s.ad}
                                {s.otomatikMi && (
                                  <span
                                    className="badge b-amber"
                                    title="İlk pakette kendiliğinden açıldı"
                                  >
                                    Otomatik
                                  </span>
                                )}
                              </div>
                              <div className="cell-sub" style={{ overflowWrap: 'anywhere' }}>
                                <span className="kod">{s.sayfaKodu}</span> ·{' '}
                                {tr(s.etiketSayisi, 0)} etiket
                              </div>
                            </td>
                            <td>
                              <span className={`badge ${sAkis.sinif}`} title={sAkis.baslik}>
                                {sAkis.etiket}
                              </span>
                            </td>
                          </tr>
                        )
                      })}

                      {acik.includes(k.id) && sayfalar.length === 0 && (
                        <tr>
                          <td />
                          <td colSpan={2} className="cell-sub" style={{ paddingLeft: 20 }}>
                            Sayfa yok — ilk pakette kendiliğinden açılır.
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  )
                })}
              </tbody>
            </table>
          </div>

          <div className="grid-foot" style={{ borderTop: '1px solid var(--line-soft)' }}>
            <span>Tekilleştirme: (kaynak, seq)</span>
          </div>
        </div>

        {secim === null && (
          <div className="card">
            <div className="insp-empty">Bir kaynak ya da sayfa seçin.</div>
          </div>
        )}

        {secim?.tur === 'kaynak' &&
          (seciliKaynakDetay ? (
            kaynakDetayi(seciliKaynakDetay)
          ) : (
            <div className="card">
              <div className="insp-empty">Yükleniyor…</div>
            </div>
          ))}

        {secim?.tur === 'sayfa' &&
          (sayfaDetay ? (
            <SayfaDetayi
              sayfa={sayfaDetay.sayfa}
              etiketler={sayfaDetay.etiketler}
              /*
                Sayfaya AİT anahtar diye bir şey yok: ingest anahtarı yalnız
                kaynağa bağlıyor, sayfayı paketin kendi page.id'si seçiyor
                (IngestUclari.cs). Bu yüzden sayfanın karşısına kaynağın tüm
                anahtarları çıkıyor — bu sayfa için üretilmiş olanlar başta.
              */
              anahtarlar={[...(seciliKaynakDetay?.anahtarlar ?? [])].sort(
                (a, b) =>
                  Number(b.sayfaId === sayfaDetay.sayfa.id) -
                  Number(a.sayfaId === sayfaDetay.sayfa.id),
              )}
              kaynakKod={seciliKaynakDetay?.kaynak.kod ?? '—'}
              yazabilir={yazabilir}
              sekme={sekme}
              setSekme={setSekme}
              silindi={() => setSecim(null)}
            />
          ) : (
            <div className="card">
              <div className="insp-empty">Yükleniyor…</div>
            </div>
          ))}
      </div>
    </>
  )
}

/**
 * Sayfa detayı ayrı bileşen: içinde çekmece açan geri çağrılar var ve
 * `useDrawer` yalnız bileşen gövdesinde çağrılabiliyor.
 */
function SayfaDetayi({
  sayfa,
  etiketler,
  anahtarlar,
  kaynakKod,
  yazabilir,
  sekme,
  setSekme,
  silindi,
}: {
  sayfa: SayfaSatiri
  etiketler: import('./api').EtiketSatiri[]
  anahtarlar: AnahtarSatiri[]
  kaynakKod: string
  yazabilir: boolean
  sekme: string
  setSekme: (a: string) => void
  /** Sayfa silindiğinde seçimi temizler — yoksa sağ panel silinen id'yi sorar. */
  silindi: () => void
}) {
  const { ac } = useDrawer()
  const akis = akisDurumu(sayfa.sonGorulmeYasiSn)

  const yeniAnahtar = () =>
    ac({
      baslik: 'Yeni API anahtarı',
      altBaslik: `${sayfa.sayfaKodu} · yalnız bu sayfa`,
      boyut: 'md',
      icerik: (
        <AnahtarDrawer
          kaynakId={sayfa.kaynakId}
          sayfaId={sayfa.id}
          kapsamAdi={sayfa.sayfaKodu}
        />
      ),
    })

  const sekmeler: Sekme[] = [
    {
      anahtar: 'baglanti',
      etiket: 'Bağlantı',
      icerik: (
        <Baglanti
          url={sayfa.ucNoktaUrl}
          saha={kaynakKod}
          sayfaKodu={sayfa.sayfaKodu}
          anahtarlar={anahtarlar}
          yazabilir={yazabilir}
          yeniAnahtar={yeniAnahtar}
        />
      ),
    },
    {
      anahtar: 'etiketler',
      etiket: 'Etiketler',
      sayac: etiketler.length,
      icerik: (
        <EtiketTablosu
          etiketler={etiketler}
          ac={(e) =>
            ac({
              baslik: e.ad ?? e.anahtar,
              altBaslik: `${sayfa.sayfaKodu} · ${e.anahtar}`,
              boyut: 'md',
              icerik: <EtiketDrawer etiket={e} yazabilir={yazabilir} />,
            })
          }
        />
      ),
    },
    {
      anahtar: 'anahtarlar',
      etiket: 'API Anahtarları',
      sayac: anahtarlar.length,
      icerik: (
        <AnahtarTablosu
          anahtarlar={anahtarlar}
          yazabilir={yazabilir}
          yeniAnahtar={yeniAnahtar}
        />
      ),
    },
    {
      anahtar: 'akis',
      etiket: 'Akış',
      icerik: (
        <>
          <OlcuTablosu
            satirlar={[
              {
                ad: 'Son görülme',
                deger: sayfa.sonGorulme ? trTarihSaat(sayfa.sonGorulme) : '—',
                not: 'Bu sayfayı taşıyan son paket',
              },
              { ad: 'Yaş', deger: yasMetni(sayfa.sonGorulmeYasiSn), not: akis.baslik },
              { ad: 'Paket sayısı', deger: tr(sayfa.paketSayisi, 0), not: '' },
              { ad: 'Etiket sayısı', deger: tr(sayfa.etiketSayisi, 0), not: '' },
              {
                ad: 'Tanım',
                deger: sayfa.otomatikMi ? 'Otomatik' : 'Elle',
                not: sayfa.otomatikMi
                  ? 'İlk pakette kendiliğinden açıldı — adlandırılmadı'
                  : 'Kullanıcı tanımladı',
              },
            ]}
          />
          {/* §1: operatör başka sayfadayken bu sayfanın verisi akmaz —
              "eski" görünmesi arıza değil. */}
          <div className="rule">
            <b>Not</b>
            <span>
              Yalnız operatörün açık tuttuğu sayfa akar; diğerleri son değerleriyle bekler.
            </span>
          </div>
        </>
      ),
    },
  ]

  return (
    <Detay
      secili={sekme}
      sec={setSekme}
      sekmeler={sekmeler}
      eyebrow={
        <>
          <span className="kod">{sayfa.sayfaKodu}</span>
          <span>·</span>
          <span>{kaynakKod}</span>
          <span>·</span>
          <span>SCADA sayfası</span>
        </>
      }
      baslik={
        <>
          {sayfa.ad}{' '}
          <span className={`badge ${akis.sinif}`} title={akis.baslik}>
            {akis.etiket}
          </span>{' '}
          <DurumRozeti durum={sayfa.durum} />
        </>
      }
      altYazi={sayfa.aciklama ?? (sayfa.otomatikMi ? 'İlk pakette kendiliğinden açıldı.' : null)}
      eylemler={
        <>
          <button
            className="btn btn-sec btn-sm"
            type="button"
            disabled={!yazabilir}
            title={yazabilir ? 'Sayfayı düzenle' : 'Bu modülde yazma yetkiniz yok'}
            onClick={() =>
              ac({
                baslik: 'Sayfayı düzenle',
                altBaslik: `${kaynakKod} · ${sayfa.sayfaKodu}`,
                boyut: 'md',
                icerik: <SayfaDrawer kaynakId={sayfa.kaynakId} mevcut={sayfa} />,
              })
            }
          >
            Düzenle
          </button>
          {/*
            Silme YAZMA yetkisi istiyor; matriste bu hak yalnız Tesis
            Sorumlusu'nda (operatör/laborant salt-okunur). Düğme de o yüzden
            `yazabilir` olmadan çıkmıyor — ayrı rol kontrolü gerekmiyor.
          */}
          <button
            className="btn btn-dan btn-sm"
            type="button"
            disabled={!yazabilir}
            title={
              yazabilir
                ? 'Sayfayı ve verisini sil (çift onay)'
                : 'Bu modülde yazma yetkiniz yok'
            }
            onClick={() =>
              ac({
                baslik: 'SCADA sayfasını sil',
                altBaslik: `${kaynakKod} · ${sayfa.sayfaKodu}`,
                boyut: 'md',
                icerik: <SayfaSilDrawer sayfa={sayfa} silindi={silindi} />,
              })
            }
          >
            Sil
          </button>
        </>
      }
    />
  )
}

export default function EntegrasyonSayfasi() {
  return (
    <Kabuk listeBasligi="SCADA Entegrasyonu">
      <YetkiKapisi modul="Entegrasyon">
        <Icerik />
      </YetkiKapisi>
    </Kabuk>
  )
}
